import React, { useEffect, useMemo,  useRef, useState } from "react";
import { trackEvent } from "../Services/Analytics";

interface TooltipProps {
  text: string;
}

const InfoTooltip: React.FC<TooltipProps> = ({ text }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const showTooltip = isHovered || isClicked;

  return (
    <span
      className="relative ml-1 inline-flex align-middle"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        aria-label="More information"
        className="
          flex h-4 w-4 items-center justify-center
          rounded-full border border-blue-500
          text-[10px] font-bold text-blue-600
          outline-none transition
          hover:bg-blue-50
          focus:bg-blue-50
          sm:h-[17px] sm:w-[17px] sm:text-[11px]
        "
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsClicked((previous) => !previous);
        }}
      >
        ?
      </button>

      {showTooltip && (
        <span
          role="tooltip"
          className="
            absolute left-0 top-full z-[9999] mt-2
            w-72 max-w-[calc(100vw-2rem)]
            rounded-md bg-gray-800
            px-3 py-2 text-left
            text-xs font-normal leading-relaxed
            text-white shadow-lg
          "
        >
          {text}

          <span
            className="
              absolute bottom-full left-4
              border-4 border-transparent
              border-b-gray-800
            "
          />
        </span>
      )}
    </span>
  );
};


interface FloorRow {
  id: number;
  drawingName: string;
  plannedArea: string;
  typicalFloor: boolean;
  additionalTypicalFloors: number;
  isFrozen: boolean;
  parentFloorId: number | null;
  typicalIndex: number | null;
}

const floorNames = [
  "Ground Floor",
  "First Floor",
  "Second Floor",
  "Third Floor",
  "Fourth Floor",
  "Fifth Floor",
  "Sixth Floor",
  "Seventh Floor",
  "Eighth Floor",
  "Ninth Floor",
  "Tenth Floor",
  "Eleventh Floor",
  "Twelfth Floor",
  "Thirteenth Floor",
  "Fourteenth Floor",
  "Fifteenth Floor",
  "Sixteenth Floor",
  "Seventeenth Floor",
  "Eighteenth Floor",
  "Nineteenth Floor",
  "Twentieth Floor",
  "Twenty First Floor",
  "Twenty Second Floor",
  "Twenty Third Floor",
  "Twenty Fourth Floor",
  "Twenty Fifth Floor",
  "Twenty Sixth Floor",
  "Twenty Seventh Floor",
  "Twenty Eighth Floor",
  "Twenty Ninth Floor",
  "Thirtieth Floor",
];

const BOQCosting: React.FC = () => {
  const [buildingType, setBuildingType] =
    useState("");

  const [gPlusFloors, setGPlusFloors] =
    useState<number | "">("");

  const [noOfBasementFloors, setNoOfBasementFloors] =
    useState<number | "">("");

  const [rows, setRows] = useState<FloorRow[]>([]);

  const [groundFloorStilt, setGroundFloorStilt] =
    useState<"" | "Yes" | "No" | "Partially">("Yes");

  const [gfResidentialPercentage, setGfResidentialPercentage] =
    useState("");

  // Enquiry details
  const [enquiryName, setEnquiryName] = useState("");
  const [enquiryMobile, setEnquiryMobile] = useState("");
  const [enquiryEmail, setEnquiryEmail] = useState("");
  const [enquiryQuery, setEnquiryQuery] = useState("");


  const projectStartedEventFired = useRef(false);
const projectDetailsStartedInitialRender = useRef(true);
const projectEventFired = useRef(false);
const boqCalculationStartedEventFired = useRef(false);
  const enquiryEventFired = useRef(false);
//const whatsappEventFired = useRef(false);

  /*
   * ============================================================
   * WHATSAPP NUMBER
   * ============================================================
   *
   * Add your WhatsApp Business number here.
   *
   * India example:
   * 919876543210
   *
   * Do not use:
   * +91 9876543210
   */
  //const WHATSAPP_NUMBER = "919XXXXXXXXX";
  const WHATSAPP_NUMBER = "917305399950";

  /*
   * ============================================================
   * CREATE FLOOR ROWS
   * ============================================================
   */

  useEffect(() => {
    if (
      gPlusFloors === "" ||
      noOfBasementFloors === ""
    ) {
      setRows([]);
      return;
    }

    const list: FloorRow[] = [];
    let id = 0;

    // Basements
    for (
      let i = noOfBasementFloors;
      i >= 1;
      i--
    ) {
      list.push({
        id: id++,
        drawingName: `B${i}`,
        plannedArea: "",
        typicalFloor: false,
        additionalTypicalFloors: 0,
        isFrozen: false,
        parentFloorId: null,
        typicalIndex: null,
      });
    }

    // Ground Floor
    list.push({
      id: id++,
      drawingName: "Ground Floor",
      plannedArea: "",
      typicalFloor: false,
      additionalTypicalFloors: 0,
      isFrozen: false,
      parentFloorId: null,
      typicalIndex: null,
    });

    // First Floor onwards
    for (
      let i = 1;
      i <= gPlusFloors;
      i++
    ) {
      list.push({
        id: id++,
        drawingName: floorNames[i],
        plannedArea: "",
        typicalFloor: false,
        additionalTypicalFloors: 0,
        isFrozen: false,
        parentFloorId: null,
        typicalIndex: null,
      });
    }

    setRows(list);
  }, [gPlusFloors, noOfBasementFloors]);


  /*
 * ============================================================
 * GA4 EVENT - PROJECT DETAILS STARTED
 * Fires once when the user enters/selects ANY project detail.
 * ============================================================
 */

useEffect(() => {
  // Do not fire the Started event on initial page load because
  // Ground Floor Stilt defaults to Yes. Fire only after user interaction.
  if (projectDetailsStartedInitialRender.current) {
    projectDetailsStartedInitialRender.current = false;
    return;
  }

  const projectDetailsStarted =
    buildingType.trim() !== "" ||
    noOfBasementFloors !== "" ||
    gPlusFloors !== "" ||
    groundFloorStilt !== "";

  if (
    projectDetailsStarted &&
    !projectStartedEventFired.current
  ) {
    projectStartedEventFired.current = true;

    trackEvent("boq_project_details_started", {
      building_type: buildingType || undefined,
      basement_floors:
        noOfBasementFloors === ""
          ? undefined
          : Number(noOfBasementFloors),
      g_plus_floors:
        gPlusFloors === ""
          ? undefined
          : Number(gPlusFloors),
      ground_floor_stilt: groundFloorStilt || undefined,
    });
  }
}, [
  buildingType,
  noOfBasementFloors,
  gPlusFloors,
  groundFloorStilt,
]);


/*
 * ============================================================
 * GA4 EVENT 1 - PROJECT DETAILS COMPLETED
 * ============================================================
 */

useEffect(() => {
  const basicProjectDetailsCompleted =
  buildingType.trim() !== "" &&
  noOfBasementFloors !== "" &&
  gPlusFloors !== "";

  // If Ground Floor is Partially,
  // percentage must also be filled.
  const partialPercentageCompleted =
    groundFloorStilt !== "Partially" ||
    gfResidentialPercentage.trim() !== "";

  const projectDetailsCompleted =
    basicProjectDetailsCompleted &&
    partialPercentageCompleted;

  if (
    projectDetailsCompleted &&
    !projectEventFired.current
  ) {
    projectEventFired.current = true;

    //sendGA4Event(
    trackEvent(
      "boq_project_details_completed",
      {
        building_type: buildingType,
        basement_floors: Number(noOfBasementFloors),
        g_plus_floors: Number(gPlusFloors),
        ground_floor_stilt: groundFloorStilt,

        ...(groundFloorStilt === "Partially" && {
          gf_residential_percentage:
            Number(gfResidentialPercentage),
        }),
      }
    );
  }
}, [
  buildingType,
  noOfBasementFloors,
  gPlusFloors,
  groundFloorStilt,
  gfResidentialPercentage,
]);



  /*
   * ============================================================
   * GA4 EVENT 3 - CALCULATION STARTED
   * Fires once when the user enters the first floor-row detail.
   * ============================================================
   */

  useEffect(() => {
    const hasStartedEnteringFloorData =
      rows.length > 0 &&
      rows.some((row) => row.plannedArea.trim() !== "");

    if (
      hasStartedEnteringFloorData &&
      !boqCalculationStartedEventFired.current
    ) {
      boqCalculationStartedEventFired.current = true;

      trackEvent("boq_calculation_started", {
        building_type: buildingType || undefined,
        basement_floors:
          noOfBasementFloors === ""
            ? undefined
            : Number(noOfBasementFloors),
        g_plus_floors:
          gPlusFloors === ""
            ? undefined
            : Number(gPlusFloors),
        ground_floor_stilt: groundFloorStilt || undefined,
        total_floor_rows: rows.length,
      });
    }
  }, [
    rows,
    buildingType,
    noOfBasementFloors,
    gPlusFloors,
    groundFloorStilt,
  ]);



  /*
   * ============================================================
   * GA4 EVENT - ENQUIRY DETAILS COMPLETED
   * Fires once when all enquiry fields are valid.
   * ============================================================
   */

  useEffect(() => {
    const enquiryDetailsCompleted =
      enquiryName.trim() !== "" &&
      enquiryMobile.trim() !== "" &&
      /^[0-9+()\-\s]{7,20}$/.test(enquiryMobile.trim()) &&
      enquiryEmail.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enquiryEmail.trim()) &&
      enquiryQuery.trim() !== "";

    if (enquiryDetailsCompleted && !enquiryEventFired.current) {
      enquiryEventFired.current = true;

      trackEvent("boq_enquiry_details_completed", {
        building_type: buildingType || undefined,
        enquiry_name: enquiryName.trim(),
        enquiry_mobile: enquiryMobile.trim(),
        enquiry_email: enquiryEmail.trim(),
      });
    }
  }, [
    enquiryName,
    enquiryMobile,
    enquiryEmail,
    enquiryQuery,
    buildingType,
  ]);

  /*
   * ============================================================
   * MAX ADDITIONAL TYPICAL FLOORS
   * ============================================================
   */

  const getMaxAdditionalTypicalFloors = (
    rowIndex: number
  ) => {
    const currentRow = rows[rowIndex];

    if (!currentRow) {
      return 0;
    }

    // Basement rows
    if (
      currentRow.drawingName.startsWith("B")
    ) {
      let count = 0;

      for (
        let i = rowIndex + 1;
        i < rows.length;
        i++
      ) {
        if (
          rows[i].drawingName.startsWith("B")
        ) {
          count++;
        } else {
          break;
        }
      }

      return count;
    }

    // Ground floor and above
    let count = 0;

    for (
      let i = rowIndex + 1;
      i < rows.length;
      i++
    ) {
      if (
        !rows[i].drawingName.startsWith("B")
      ) {
        count++;
      }
    }

    return count;
  };

  /*
   * ============================================================
   * UPDATE TYPICAL FLOORS
   * ============================================================
   */

  const updateTypicalFloors = (
    parentIndex: number,
    count: number
  ) => {
    setRows((prev) => {
      const updated = prev.map((r) => ({
        ...r,
      }));

      const parent = updated[parentIndex];

      if (!parent) {
        return updated;
      }

      /*
       * Calculate the maximum using the current
       * row structure rather than stale state.
       */
      let maxAllowed = 0;

      if (
        parent.drawingName.startsWith("B")
      ) {
        for (
          let i = parentIndex + 1;
          i < updated.length;
          i++
        ) {
          if (
            updated[i].drawingName.startsWith(
              "B"
            )
          ) {
            maxAllowed++;
          } else {
            break;
          }
        }
      } else {
        for (
          let i = parentIndex + 1;
          i < updated.length;
          i++
        ) {
          if (
            !updated[i].drawingName.startsWith(
              "B"
            )
          ) {
            maxAllowed++;
          }
        }
      }

      const actualCount = Math.min(
        Math.max(count, 0),
        maxAllowed
      );

      parent.additionalTypicalFloors =
        actualCount;

      parent.typicalFloor =
        actualCount > 0;

      /*
       * Remove old children
       */
      for (
        let i = parentIndex + 1;
        i < updated.length;
        i++
      ) {
        if (
          updated[i].parentFloorId ===
          parent.id
        ) {
          updated[i].isFrozen = false;
          updated[i].parentFloorId = null;
          updated[i].typicalIndex = null;
        }
      }

      /*
       * Add new children
       */
      for (
        let i = 1;
        i <= actualCount;
        i++
      ) {
        const child =
          updated[parentIndex + i];

        if (!child) {
          continue;
        }

        child.isFrozen = true;
        child.parentFloorId = parent.id;
        child.typicalIndex = i;
        child.plannedArea =
          parent.plannedArea;
      }

      return updated;
    });
  };

  /*
   * ============================================================
   * UPDATE PLANNED AREA
   * ============================================================
   */

  const updatePlannedArea = (
    rowIndex: number,
    value: string
  ) => {
    setRows((prev) => {
      const updated = [...prev];

      if (!updated[rowIndex]) {
        return updated;
      }

      updated[rowIndex].plannedArea =
        value;

      const parentId =
        updated[rowIndex].id;

      updated.forEach((r) => {
        if (
          r.parentFloorId === parentId
        ) {
          r.plannedArea = value;
        }
      });

      return [...updated];
    });
  };

  /*
   * ============================================================
   * AREA CALCULATIONS
   * ============================================================
   */

  const calculateBasementEffectiveArea =
    () => {
      let basementArea = 0;

      rows.forEach((row) => {
        if (
          row.drawingName.startsWith("B")
        ) {
          basementArea += Number(
            row.plannedArea || 0
          );
        }
      });

      return basementArea;
    };

  const getBasementArea = () => {
    return rows
      .filter(
        (row) =>
          row.drawingName.startsWith("B") &&
          !row.isFrozen
      )
      .reduce(
        (sum, row) =>
          sum +
          Number(row.plannedArea || 0),
        0
      );
  };

  const getTypicalBasementArea = () => {
    return rows
      .filter(
        (row) =>
          row.drawingName.startsWith("B") &&
          row.typicalFloor &&
          !row.isFrozen
      )
      .reduce(
        (sum, row) =>
          sum +
          Number(row.plannedArea || 0),
        0
      );
  };

  const getGroundFloorArea = () => {
    const ground = rows.find(
      (row) =>
        row.drawingName ===
        "Ground Floor"
    );

    return Number(
      ground?.plannedArea || 0
    );
  };

  const getUniqueFloorCount = () => {
    return rows.filter(
      (row) =>
        !row.drawingName.startsWith("B") &&
        row.drawingName !==
          "Ground Floor" &&
        !row.isFrozen
    ).length;
  };

  const getUniqueFloorArea = () => {
    return rows
      .filter(
        (row) =>
          !row.drawingName.startsWith("B") &&
          row.drawingName !==
            "Ground Floor" &&
          !row.isFrozen
      )
      .reduce(
        (sum, row) =>
          sum +
          Number(row.plannedArea || 0),
        0
      );
  };

  const getTypicalGroups = () => {
    return rows
      .filter(
        (row) =>
          row.typicalFloor &&
          !row.isFrozen &&
          row.additionalTypicalFloors > 0
      )
      .map((row) => ({
        drawingName: row.drawingName,
        floors:
          row.additionalTypicalFloors,
        area: Number(
          row.plannedArea || 0
        ),
      }));
  };

  const getTotalTypicalBasementArea =
    () => {
      return rows
        .filter(
          (row) =>
            row.drawingName.startsWith("B") &&
            row.typicalFloor &&
            !row.isFrozen
        )
        .reduce(
          (sum, row) =>
            sum +
            Number(row.plannedArea || 0),
          0
        );
    };

  const getTotalAreaOfAllTypicalBasementFloors =
    () => {
      return rows
        .filter(
          (row) =>
            row.drawingName.startsWith("B") &&
            row.typicalFloor &&
            !row.isFrozen
        )
        .reduce(
          (sum, row) =>
            sum +
            Number(row.plannedArea || 0) *
              (row.additionalTypicalFloors +
                1),
          0
        );
    };

  const getTotalAreaOfAllTypicalSuperStructureFloors =
    () => {
      return rows
        .filter(
          (row) =>
            !row.drawingName.startsWith("B") &&
            row.drawingName !==
              "Ground Floor" &&
            row.typicalFloor &&
            !row.isFrozen
        )
        .reduce(
          (sum, row) =>
            sum +
            Number(row.plannedArea || 0) *
              (row.additionalTypicalFloors +
                1),
          0
        );
    };

  const getTotalTypicalSuperStructureArea =
    () => {
      return getTypicalGroups()
        .filter(
          (group) =>
            !group.drawingName.startsWith(
              "B"
            )
        )
        .reduce(
          (sum, group) =>
            sum +
            group.area * group.floors,
          0
        );
    };

  const getNumberOfTypicalFloors = () => {
    return getTypicalGroups().reduce(
      (sum, group) =>
        sum + group.floors,
      0
    );
  };

  const getOverallArea = () => {
    return (
      getBasementArea() +
      getTypicalBasementArea() +
      getGroundFloorArea() +
      getUniqueFloorArea() +
      getTotalTypicalSuperStructureArea()
    );
  };

  const getTotalTypicalFloorArea = () => {
    return (
      getTotalTypicalBasementArea() +
      getTotalTypicalSuperStructureArea()
    );
  };

  const getTotalUniqueFloorArea = () => {
    return (
      getBasementArea() +
      getGroundFloorArea() +
      getUniqueFloorArea()
    );
  };

  const getTotalEffectiveFloorArea = () => {
    return (
      getTotalUniqueFloorArea() +
      0.5 *
        getTotalTypicalFloorArea()
    );
  };

  const getTotalEffectiveArea = () => {
    return getTotalEffectiveFloorArea();
  };

  const getTypicalEffectiveArea = () => {
    return getTypicalGroups().reduce(
      (sum, group) =>
        sum +
        group.area * group.floors,
      0
    );
  };

  /*
   * ============================================================
   * COST CALCULATIONS
   * ============================================================
   */

  const RESIDENTIAL_BASE_AMOUNT =
    30000;

  const COMMERCIAL_BASE_AMOUNT =
    25000;

  const getResidentialCost = () => {
    const effectiveArea =
      getTotalEffectiveFloorArea();

    const uniqueArea =
      getTotalUniqueFloorArea();

    const typicalFloors =
      getNumberOfTypicalFloors();

    if (effectiveArea > 100000) {
      return null;
    }

    if (effectiveArea <= 10000) {
      return RESIDENTIAL_BASE_AMOUNT;
    }

    if (effectiveArea <= 25000) {
      return (
        RESIDENTIAL_BASE_AMOUNT +
        (uniqueArea - 10000) * 3 +
        typicalFloors * 10000
      );
    }

    if (effectiveArea <= 50000) {
      return (
        RESIDENTIAL_BASE_AMOUNT +
        (uniqueArea - 15000) * 3 +
        typicalFloors * 10000
      );
    }

    return (
      RESIDENTIAL_BASE_AMOUNT +
      (uniqueArea - 35000) * 3 +
      typicalFloors * 10000
    );
  };

  const getCommercialCost = () => {
    const effectiveArea =
      getTotalEffectiveFloorArea();

    const uniqueArea =
      getTotalUniqueFloorArea();

    const typicalFloors =
      getNumberOfTypicalFloors();

    if (effectiveArea > 100000) {
      return null;
    }

    if (effectiveArea <= 10000) {
      return COMMERCIAL_BASE_AMOUNT;
    }

    if (effectiveArea <= 25000) {
      return (
        COMMERCIAL_BASE_AMOUNT +
        (uniqueArea - 10000) * 2.5 +
        typicalFloors * 10000
      );
    }

    if (effectiveArea <= 50000) {
      return (
        COMMERCIAL_BASE_AMOUNT +
        15000 * 2.5 +
        (uniqueArea - 25000) *
          2.25 +
        typicalFloors * 10000
      );
    }

    return (
      COMMERCIAL_BASE_AMOUNT +
      15000 * 2.5 +
      25000 * 2.25 +
      (uniqueArea - 50000) * 2 +
      typicalFloors * 10000
    );
  };

  /*
   * ============================================================
   * WHATSAPP SUBMIT
   * ============================================================
   */

  const handleSaveBOQ = () => {
    try {
      /*
       * Complete BOQ data
       */
      const data = {
        enquiryName: enquiryName.trim(),
        enquiryMobile: enquiryMobile.trim(),
        enquiryEmail: enquiryEmail.trim(),
        enquiryQuery: enquiryQuery.trim(),

        buildingType:
          buildingType.trim(),

        noOfBasementFloors:
          noOfBasementFloors === ""
            ? null
            : Number(
                noOfBasementFloors
              ),

        gPlusFloors:
          gPlusFloors === ""
            ? null
            : Number(gPlusFloors),

        groundFloorStilt,

        gfResidentialPercentage:
          gfResidentialPercentage === ""
            ? null
            : Number(
                gfResidentialPercentage
              ),

        uniqueBasementArea:
          getBasementArea(),

        typicalBasementArea:
          getTypicalBasementArea(),

        groundFloorArea:
          getGroundFloorArea(),

        uniqueFloorArea:
          getUniqueFloorArea(),

        totalTypicalSuperStructureArea:
          getTotalTypicalSuperStructureArea(),

        overallArea:
          getOverallArea(),

        totalTypicalFloorArea:
          getTotalTypicalFloorArea(),

        totalUniqueFloorArea:
          getTotalUniqueFloorArea(),

        totalEffectiveFloorArea:
          getTotalEffectiveFloorArea(),

        typicalEffectiveArea:
          getTypicalEffectiveArea(),

        boqAmount:
          buildingType.trim() ===
          "Residential"
            ? getResidentialCost()
            : getCommercialCost(),

        floors: rows.map((row) => ({
          drawingName:
            row.drawingName,

          plannedArea:
            row.plannedArea === ""
              ? null
              : Number(
                  row.plannedArea
                ),

          typicalFloor:
            row.typicalFloor,

          additionalTypicalFloors:
            row.additionalTypicalFloors,

          isFrozen:
            row.isFrozen,

          parentFloorId:
            row.parentFloorId,

          typicalIndex:
            row.typicalIndex,
        })),
      };

      // Validate enquiry details before opening WhatsApp
      if (!data.enquiryName) {
        alert("Please enter your name.");
        return;
      }

      if (!data.enquiryMobile) {
        alert("Please enter your mobile number.");
        return;
      }

      if (!/^[0-9+()\-\s]{7,20}$/.test(data.enquiryMobile)) {
        alert("Please enter a valid mobile number.");
        return;
      }

      if (!data.enquiryEmail) {
        alert("Please enter your email address.");
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.enquiryEmail)) {
        alert("Please enter a valid email address.");
        return;
      }

      if (!data.enquiryQuery) {
        alert("Please enter your query.");
        return;
      }


      console.log(
        "Complete BOQ Data:",
        data
      );

      /*
       * Format area
       */
      const formatArea = (
        value: number | null
      ) => {
        if (
          value === null ||
          value === undefined
        ) {
          return "0";
        }

        return value.toLocaleString(
          "en-IN"
        );
      };

      /*
       * Format amount
       */
      const formatAmount = (
        value: number | null
      ) => {
        if (
          value === null ||
          value === undefined
        ) {
          return "₹0";
        }

        return `₹${value.toLocaleString(
          "en-IN"
        )}`;
      };

      /*
       * Floor details
       */
      const floorDetails =
        data.floors
          .map((floor, index) => {
            return (
              `${index + 1}. *${
                floor.drawingName
              }*\n` +
              `   Planned Area: ${formatArea(
                floor.plannedArea
              )} sq.ft\n` +
              `   Repetitive Floor: ${
                floor.typicalFloor
                  ? "Yes"
                  : "No"
              }\n` +
              `   Additional Repetitive Floors: ${
                floor.additionalTypicalFloors
              }`
            );
          })
          .join("\n\n");

      /*
       * WhatsApp message
       */
      const message = `
📩 *NEW BOQ ENQUIRY*

━━━━━━━━━━━━━━━━━━
*CUSTOMER DETAILS*
━━━━━━━━━━━━━━━━━━

Name: ${data.enquiryName}
Mobile: ${data.enquiryMobile}
Email: ${data.enquiryEmail}
Query: ${data.enquiryQuery}

━━━━━━━━━━━━━━━━━━
*BOQ FEES STRUCTURE*
━━━━━━━━━━━━━━━━━━

*PROJECT DETAILS*
━━━━━━━━━━━━━━━━━━

Building Type: ${data.buildingType}

No. of Basement Floors: ${
        data.noOfBasementFloors ?? 0
      }

G + Floors: ${
        data.gPlusFloors ?? 0
      }

Ground Floor Stilt: ${
        data.groundFloorStilt
      }

GF Residential Percentage: ${
        data.gfResidentialPercentage ??
        0
      }%

━━━━━━━━━━━━━━━━━━
*AREA SUMMARY*
━━━━━━━━━━━━━━━━━━

Unique Basement Area: ${formatArea(
        data.uniqueBasementArea
      )} sq.ft

Typical Basement Area: ${formatArea(
        data.typicalBasementArea
      )} sq.ft

Ground Floor Area: ${formatArea(
        data.groundFloorArea
      )} sq.ft

Unique Floor Area: ${formatArea(
        data.uniqueFloorArea
      )} sq.ft

Total Typical Super Structure Area: ${formatArea(
        data.totalTypicalSuperStructureArea
      )} sq.ft

Overall Area: ${formatArea(
        data.overallArea
      )} sq.ft

━━━━━━━━━━━━━━━━━━
*FLOOR DETAILS*
━━━━━━━━━━━━━━━━━━

${floorDetails}

━━━━━━━━━━━━━━━━━━
*BOQ FEES*
━━━━━━━━━━━━━━━━━━

*${formatAmount(
        data.boqAmount
      )}*

━━━━━━━━━━━━━━━━━━


`;

      /*
       * Check WhatsApp number
       */
      if (
        !WHATSAPP_NUMBER ||
        WHATSAPP_NUMBER.includes(
          "XXXXXXXXX"
        )
      ) {
        alert(
          "Please enter your WhatsApp Business number in WHATSAPP_NUMBER."
        );

        return;
      }

      /*
       * Generate WhatsApp link
       */
      const whatsappUrl =
        `https://wa.me/${WHATSAPP_NUMBER}` +
        `?text=${encodeURIComponent(
          message.trim()
        )}`;

      console.log(
        "WhatsApp URL:",
        whatsappUrl
      );

      /*
       * Open WhatsApp
       */
      window.open(
        whatsappUrl,
        "_blank",
        "noopener,noreferrer"
      );

    } catch (error) {
      console.error(
        "Error preparing BOQ for WhatsApp:",
        error
      );

      alert(
        "Unable to prepare the BOQ for WhatsApp."
      );
    }
  };

  /*
   * ============================================================
   * TOGGLE TYPICAL
   * ============================================================
   */

  const toggleTypical = (
    rowIndex: number,
    yes: boolean
  ) => {
    if (!yes) {
      updateTypicalFloors(
        rowIndex,
        0
      );

      return;
    }

    setRows((prev) => {
      const updated = [...prev];

      if (updated[rowIndex]) {
        updated[rowIndex].typicalFloor =
          true;
      }

      return updated;
    });
  };

  /*
   * ============================================================
   * LAST ROW
   * ============================================================
   */

  const lastRowIndex = useMemo(
    () => rows.length - 1,
    [rows]
  );

  const showAdditionalColumn =
    rows.some(
      (row) => row.typicalFloor
    );

  /*
   * ============================================================
   * UI
   * ============================================================
   */

  return (
    <div className="w-full min-w-0 overflow-x-hidden bg-gray-100 p-2 sm:p-4 md:p-6">

      <div className="mx-auto w-full max-w-7xl overflow-hidden rounded-lg bg-white shadow">

        {/* HEADER */}

        <div className="rounded-t-lg bg-blue-700 px-3 py-3 sm:px-6 sm:py-4">

          <h2 className="text-lg font-semibold text-white sm:text-xl md:text-2xl">
            BOQ Fees Structure
          </h2>

        </div>

        <div className="min-w-0 p-3 sm:p-4 md:p-6">

          {/* ================================================= */}
          {/* PROJECT INPUTS */}
          {/* ================================================= */}

          <div className="mb-6 grid grid-cols-1 gap-5 md:grid-cols-2">

            {/* BUILDING TYPE */}

            <div className="min-w-0">

              <label className="mb-2 block text-sm font-medium text-gray-700">
                Building Type
              </label>

              <select
                value={buildingType}
                onChange={(e) =>
                  setBuildingType(
                    e.target.value
                  )
                }
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 outline-none focus:border-blue-500"
              >

                <option value="">
                  Select Building Type
                </option>

                <option value="Residential">
                  Residential
                </option>

                <option>
                  Commercial
                </option>

              </select>

            </div>

            {/* BASEMENT */}

            <div className="min-w-0">
{/* 
              <label className="mb-2 block text-sm font-medium text-gray-700">
                No. of Basement Floors
              </label>
 */}
 <label className="mb-2 block text-sm font-medium text-gray-700">
  <span className="inline-flex items-center">
    No. of Basement Floors
    <InfoTooltip text="No. of underground Floors" />
  </span>
</label>
              <input
                type="number"
                min={0}
                value={
                  noOfBasementFloors
                }
                onChange={(e) =>
                  setNoOfBasementFloors(
                    e.target.value ===
                      ""
                      ? ""
                      : Number(
                          e.target.value
                        )
                  )
                }
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
              />

            </div>

            {/* G+ FLOORS */}

            <div className="min-w-0">

             {/*  <label className="mb-2 block text-sm font-medium text-gray-700">
                G + Floors (Including Ground Floor)
              </label>
 */}
 <label className="mb-2 block text-sm font-medium text-gray-700">
  <span className="inline-flex items-center">
    G + Floors (Inclu Ground Floor)
    <InfoTooltip text="Ex: GF + 2 UF= 3 Floors" />
  </span>
</label>
              <input
                type="number"
                min={0}
                value={gPlusFloors}
                onChange={(e) =>
                  setGPlusFloors(
                    e.target.value ===
                      ""
                      ? ""
                      : Number(
                          e.target.value
                        )
                  )
                }
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
              />

            </div>

            {/* STILT */}

            <div className="min-w-0">
{/* 
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Is Ground Floor Stilt?
              </label> */}
              <label className="mb-2 block text-sm font-medium text-gray-700">
  <span className="inline-flex items-center">
    Is Ground Floor Stilt?
    <InfoTooltip
      text="Stilt: Open floor used for parking."
    />
  </span>
</label>

              <div className="flex flex-wrap gap-4 sm:gap-8">

                <label className="flex items-center gap-2">

                  <input
                    type="radio"
                    checked={
                      groundFloorStilt ===
                      "Yes"
                    }
                    onChange={() => {
                      setGroundFloorStilt(
                        "Yes"
                      );

                      setGfResidentialPercentage(
                        "0"
                      );
                    }}
                  />

                  Yes

                </label>

                <label className="flex items-center gap-2">

                  <input
                    type="radio"
                    checked={
                      groundFloorStilt ===
                      "No"
                    }
                    onChange={() => {
                      setGroundFloorStilt(
                        "No"
                      );

                      setGfResidentialPercentage(
                        "100"
                      );
                    }}
                  />

                  No

                </label>

                <label className="flex items-center gap-2">

                  <input
                    type="radio"
                    checked={
                      groundFloorStilt ===
                      "Partially"
                    }
                    onChange={() => {
                      setGroundFloorStilt(
                        "Partially"
                      );

                      setGfResidentialPercentage(
                        ""
                      );
                    }}
                  />

                  Partially

                </label>

              </div>

            </div>

            {/* PARTIAL */}

            {groundFloorStilt ===
              "Partially" && (

              <div className="min-w-0 md:col-span-2">

                <label className="mb-2 block text-sm font-medium text-gray-700">
                  % of GF occupied for Residential Purpose
                </label>

                <input
                  type="number"
                  min={0}
                  max={100}
                  value={
                    gfResidentialPercentage
                  }
                  onChange={(e) =>
                    setGfResidentialPercentage(
                      e.target.value
                    )
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                />

              </div>

            )}

          </div>

          {/* ================================================= */}
          {/* RESPONSIVE TABLE */}
          {/* ================================================= */}

          <div className="w-full min-w-0 overflow-hidden rounded-lg border border-gray-300">

            <table className="w-full table-fixed border-collapse text-[9px] sm:text-xs md:text-sm">

              <thead className="bg-gray-200">

                <tr>

                  {/* S.NO */}

                  <th
                    className="
                      w-[9%]
                      border
                      border-gray-300
                      px-0.5
                      py-2
                      text-center
                      font-semibold
                      sm:w-[8%]
                      sm:px-2
                      sm:py-3
                    "
                  >
                    S.No
                  </th>

                  {/* DRAWING */}

                  <th
                    className="
                      w-[25%]
                      border
                      border-gray-300
                      px-1
                      py-2
                      text-left
                      font-semibold
                      sm:w-[24%]
                      sm:px-2
                      sm:py-3
                    "
                  >
                    Drawing
                  </th>

                  {/* PLANNED */}

                  <th
                    className="
                      w-[24%]
                      border
                      border-gray-300
                      px-1
                      py-2
                      text-left
                      font-semibold
                      sm:w-[23%]
                      sm:px-2
                      sm:py-3
                    "
                  >
                    Planned Area
                  </th>

                  {/* REPETITIVE */}

                  <th
                    className={`
                      border
                      border-gray-300
                      px-1
                      py-2
                      text-center
                      font-semibold
                      ${
                        showAdditionalColumn
                          ? "w-[21%]"
                          : "w-[42%]"
                      }
                    `}
                  >
                    Repetitive
                  </th>

                  {/* ADDITIONAL */}

                  {showAdditionalColumn && (

                    <th
                      className="
                        w-[21%]
                        border
                        border-gray-300
                        px-1
                        py-2
                        text-center
                        font-semibold
                        sm:w-[25%]
                        sm:px-2
                        sm:py-3
                      "
                    >
                      <span className="hidden sm:inline">
                        Additional Repetitive
                      </span>

                      <span className="sm:hidden">
                        Additional
                      </span>

                    </th>

                  )}

                </tr>

              </thead>

              <tbody>

                {rows.map(
                  (row, index) => {

                    const isLastRow =
                      index ===
                      lastRowIndex;

                    return (

                      <tr
                        key={row.id}
                        className={
                          row.isFrozen
                            ? "bg-blue-50"
                            : "bg-white"
                        }
                      >

                        {/* S.NO */}

                        <td
                          className="
                            border
                            border-gray-300
                            px-1
                            py-2
                            text-center
                            align-middle
                            sm:px-2
                            sm:py-3
                          "
                        >
                          {index + 1}
                        </td>

                        {/* DRAWING NAME */}

                        <td
                          className="
                            border
                            border-gray-300
                            px-1
                            py-2
                            align-middle
                            sm:px-2
                            sm:py-3
                          "
                        >

                          <div
                            className="
                              break-words
                              text-[9px]
                              font-medium
                              leading-tight
                              text-gray-700
                              sm:text-xs
                              md:text-sm
                            "
                          >
                            {
                              row.drawingName
                            }
                          </div>

                        </td>

                        {/* PLANNED AREA */}

                        <td
                          className="
                            border
                            border-gray-300
                            px-1
                            py-2
                            align-middle
                            sm:px-2
                            sm:py-3
                          "
                        >

                          <input
                            type="number"
                            value={
                              row.plannedArea
                            }
                            readOnly={
                              row.isFrozen
                            }
                            onChange={(e) =>
                              updatePlannedArea(
                                index,
                                e.target.value
                              )
                            }
                            className={`
                              w-full
                              min-w-0
                              rounded
                              border
                              border-gray-300
                              px-1
                              py-1.5
                              text-[9px]
                              outline-none
                              focus:border-blue-500
                              sm:px-2
                              sm:py-2
                              sm:text-xs
                              md:text-sm
                              ${
                                row.isFrozen
                                  ? "bg-gray-100"
                                  : "bg-white"
                              }
                            `}
                          />

                        </td>

                        {/* REPETITIVE */}

                        {!isLastRow &&
                        !row.isFrozen ? (

                          <td
                            className="
                              border
                              border-gray-300
                              px-1
                              py-2
                              align-middle
                              sm:px-2
                              sm:py-3
                            "
                          >

                            <div
                              className="
                                flex
                                flex-col
                                items-center
                                justify-center
                                gap-1
                                sm:flex-row
                                sm:gap-3
                              "
                            >

                              <label
                                className="
                                  flex
                                  cursor-pointer
                                  items-center
                                  gap-1
                                "
                              >

                                <input
                                  type="checkbox"
                                  checked={
                                    row.typicalFloor
                                  }
                                  onChange={() =>
                                    toggleTypical(
                                      index,
                                      true
                                    )
                                  }
                                  className="
                                    h-3
                                    w-3
                                    sm:h-4
                                    sm:w-4
                                  "
                                />

                                <span className="text-[9px] sm:text-xs">
                                  Yes
                                </span>

                              </label>

                              <label
                                className="
                                  flex
                                  cursor-pointer
                                  items-center
                                  gap-1
                                "
                              >

                                <input
                                  type="checkbox"
                                  checked={
                                    !row.typicalFloor
                                  }
                                  onChange={() =>
                                    toggleTypical(
                                      index,
                                      false
                                    )
                                  }
                                  className="
                                    h-3
                                    w-3
                                    sm:h-4
                                    sm:w-4
                                  "
                                />

                                <span className="text-[9px] sm:text-xs">
                                  No
                                </span>

                              </label>

                            </div>

                          </td>

                        ) : (

                          <td
                            className="
                              border
                              border-gray-300
                              bg-gray-50
                            "
                          />

                        )}

                        {/* ADDITIONAL REPETITIVE */}

                        {showAdditionalColumn && (

                          !isLastRow &&
                          !row.isFrozen ? (

                            <td
                              className="
                                border
                                border-gray-300
                                px-1
                                py-2
                                align-middle
                                sm:px-2
                                sm:py-3
                              "
                            >

                              {row.typicalFloor && (

                                <select
                                  value={
                                    row.additionalTypicalFloors
                                  }
                                  onChange={(e) =>
                                    updateTypicalFloors(
                                      index,
                                      Number(
                                        e.target
                                          .value
                                      )
                                    )
                                  }
                                  className="
                                    w-full
                                    min-w-0
                                    rounded
                                    border
                                    border-gray-300
                                    bg-white
                                    px-1
                                    py-1.5
                                    text-[9px]
                                    outline-none
                                    focus:border-blue-500
                                    sm:px-2
                                    sm:py-2
                                    sm:text-xs
                                  "
                                >

                                  {Array.from(
                                    {
                                      length:
                                        getMaxAdditionalTypicalFloors(
                                          index
                                        ) +
                                        1,
                                    },
                                    (
                                      _,
                                      i
                                    ) => (

                                      <option
                                        key={i}
                                        value={i}
                                      >
                                        {i}
                                      </option>

                                    )
                                  )}

                                </select>

                              )}

                            </td>

                          ) : (

                            <td
                              className="
                                border
                                border-gray-300
                                bg-gray-50
                              "
                            />

                          )

                        )}

                      </tr>

                    );
                  }
                )}

              </tbody>

            </table>

          </div>

          {/* ================================================= */}
          {/* AREA SUMMARY */}
          {/* ================================================= */}

          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">

            <h3 className="mb-4 text-lg font-semibold text-gray-800">
              Area Summary
            </h3>

            <div className="grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">

              <div>
                Unique Basement Area :
                <span className="ml-1 font-medium">
                  {getBasementArea()}
                </span>
              </div>

              <div>
                Typical Basement Area :
                <span className="ml-1 font-medium">
                  {
                    getTypicalBasementArea()
                  }
                </span>
              </div>

              <div>
                Ground Floor Area :
                <span className="ml-1 font-medium">
                  {
                    getGroundFloorArea()
                  }
                </span>
              </div>

              <div>
                Unique Floor Area :
                <span className="ml-1 font-medium">
                  {
                    getUniqueFloorArea()
                  }
                </span>
              </div>

              <div>
                Total Typical Super Structure Area :
                <span className="ml-1 font-medium">
                  {
                    getTotalTypicalSuperStructureArea()
                  }
                </span>
              </div>

              <div className="font-bold text-gray-900">
                Overall Area :
                <span className="ml-1">
                  {getOverallArea()}
                </span>
              </div>

            </div>

          </div>

          {/* ================================================= */}
          {/* BOQ FEE CALCULATION */}
          {/* ================================================= */}

          {buildingType && (

          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">

            {buildingType ===
            "Residential" ? (

              <div className="font-semibold text-gray-800">

                Residential BOQ Fees Structure :

                <span className="ml-2 text-lg text-blue-700">
                  ₹
                  {getResidentialCost()?.toLocaleString(
                    "en-IN"
                  )}

                </span>

              </div>

            ) : (

              <div className="font-semibold text-gray-800">

                Commercial BOQ Fees Structure :

                <span className="ml-2 text-lg text-blue-700">
                  ₹
                  {getCommercialCost()?.toLocaleString(
                    "en-IN"
                  )}

                </span>

              </div>

            )}

          </div>

          )}

          {/* ================================================= */}
          {/* ENQUIRY DETAILS - AFTER BOQ FEE CALCULATION */}
          {/* ================================================= */}

          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">

            <h3 className="mb-4 text-base font-semibold text-gray-800 sm:text-lg">
              Enquiry Details
            </h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

              {/* NAME */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  value={enquiryName}
                  onChange={(e) => setEnquiryName(e.target.value)}
                  placeholder="Enter your name"
                  autoComplete="name"
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </div>

              {/* MOBILE */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Mobile Number <span className="text-red-500">*</span>
                </label>

                <input
                  type="tel"
                  value={enquiryMobile}
                  onChange={(e) => setEnquiryMobile(e.target.value)}
                  placeholder="Enter mobile number"
                  inputMode="tel"
                  autoComplete="tel"
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </div>

              {/* EMAIL */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>

                <input
                  type="email"
                  value={enquiryEmail}
                  onChange={(e) => setEnquiryEmail(e.target.value)}
                  placeholder="Enter email address"
                  inputMode="email"
                  autoComplete="email"
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </div>

              {/* QUERY */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Query / Enquiry <span className="text-red-500">*</span>
                </label>

                <textarea
                  value={enquiryQuery}
                  onChange={(e) => setEnquiryQuery(e.target.value)}
                  placeholder="Enter your enquiry"
                  rows={3}
                  className="w-full resize-none rounded border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </div>

            </div>

          </div>

          {/* WHATSAPP BUTTON */}

          <button
            type="button"
            onClick={
              handleSaveBOQ
            }
            className="
              mt-5
              flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-lg
              bg-green-600
              px-5
              py-3
              font-semibold
              text-white
              shadow-sm
              transition
              hover:bg-green-700
              active:scale-[0.99]
              sm:w-auto
            "
          >

            

            <span>
              Submit Enquiry via Whatsapp
            </span>

          </button>

          <p className="mt-2 text-xs text-gray-500">
            Your enquiry details and complete BOQ details will be
            prepared and opened in WhatsApp.
          </p>


        </div>

      </div>

    </div>
  );
};

export default BOQCosting;